import { useTrackingContext } from "#app/TrackingProvider.tsx";
import { useDisabledChannelsStore } from "#features/channels/stores/useDisabledChannelsStore.ts";
import { useCreateConversationMessageMutation } from "#features/conversations/api/useCreateConversationMessageMutation.ts";
import { BlockedError, StreamError } from "#features/conversations/hooks/useConversationMessageStreamHandler.ts";
import { cancelPendingConnection } from "#features/conversations/lib/connectionRecords.ts";
import { cancelPendingQuestion } from "#features/conversations/lib/questionRecords.ts";
import { cancelPendingApproval } from "#features/conversations/lib/toolApprovalRecords.ts";
import { useAskInChatStore } from "#features/conversations/stores/useAskInChatStore.ts";
import { useComposerDraftStore } from "#features/conversations/stores/useComposerDraftStore.ts";
import {
    ASSISTANT_MODE_BY_EFFORT,
    MIXPANEL_ASSISTANT_MODE,
    useAssistantModeStore
} from "#features/conversations/stores/useAssistantModeStore.ts";
import { usePromptOptionsStore } from "#features/conversations/stores/usePromptOptionsStore.ts";
import type { FileModel } from "#features/conversations/types/FileModel.ts";
import { parseSelectedSkills } from "#features/conversations/utils/parseSelectedSkills.ts";
import { useFeatureFlag } from "#hooks/useFeatureFlag.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import type { InjectedSourceWithSourceType } from "#types/injectedSource.ts";
import type { PageActionModel } from "#types/PageActionModel.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import type { Conversation, FunctionName, LlmId, ResumeEntry, Skill, SkillTemplateSelection } from "#types/types.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

export interface CreateConversationMessageOptions {
    files?: FileModel[];
    additionalInstructions?: string;
    disableSearch?: boolean;
    context?: string;
    sources?: InjectedSourceWithSourceType[];
    isDefaultSuggestion?: boolean;
    promptFeature?: string;
    pageActions?: PageActionModel[];
    selectedSkills?: Record<string, SkillTemplateSelection>;
    llmId?: LlmId;
    existingConversationId?: string;
    resume?: ResumeEntry[];
    preApprovedTools?: FunctionName[];
    onMutate?: () => void;
    onError?: () => void;
    onSettled?: () => void;
}

export const useCreateConversationMessage = () => {
    const { trackEvent } = useTracking();
    const { trackingAppIdentifier } = useTrackingContext();
    const { mutate } = useCreateConversationMessageMutation();
    const promptOptions = usePromptOptionsStore(state => state.promptOptions);
    const targetedReply = useAskInChatStore(state => state.askInChatOptions?.targetedReply);
    const reset = useAskInChatStore(state => state.reset);
    const reasoningEffort = useAssistantModeStore(state => state.reasoningEffort);
    const disabledChannels = useDisabledChannelsStore(state => state.disabledChannels);
    const { hasFeatureFlag } = useFeatureFlag();
    const isSlashCommandPickerEnabled = hasFeatureFlag("SlashCommandPicker");
    const assistantMode = ASSISTANT_MODE_BY_EFFORT[reasoningEffort];
    const queryClient = useQueryClient();

    const createConversationMessage = useCallback((
        conversationId: string,
        content: string,
        {
            files = [],
            additionalInstructions,
            disableSearch,
            context,
            sources,
            isDefaultSuggestion = false,
            promptFeature,
            pageActions,
            selectedSkills,
            llmId,
            resume,
            preApprovedTools,
            onMutate,
            onError,
            onSettled
        }: CreateConversationMessageOptions = {}
    ) => {
        const completedFiles = files.filter(file => file.fileUploadStatus === "Completed");

        const conversation = queryClient.getQueryData<Conversation>(
            AiQueryKeys.GetConversation(conversationId)
        );

        // The Skills Management admin surface primes the same cache key via a suspense hook,
        // so relying on "cache empty ⇒ no slugs" isn't enough to keep this dormant when the
        // SlashCommandPicker flag is off. Gate the parse on the flag directly.
        const cachedSkills = isSlashCommandPickerEnabled
            ? queryClient.getQueryData<Skill[]>(AiQueryKeys.GetSkills()) ?? []
            : [];
        const parsedSkills = parseSelectedSkills(content, cachedSkills);
        // This message's skills win over the page's. Undefined keeps the conversation's skills.
        const derivedSelectedSkills = selectedSkills ?? parsedSkills ?? promptOptions?.selectedSkills;

        // The card sends its own decisions, so only other sends cancel the open approval, question or connection request.
        const hasCancelled = resume === undefined && [cancelPendingApproval(queryClient, conversationId), cancelPendingQuestion(queryClient, conversationId), cancelPendingConnection(queryClient, conversationId)].some(Boolean);

        const featureValue = promptFeature
            ?? promptOptions?.promptFeature
            ?? (conversation?.discriminator === "smart_report" ? "Smart Report" : undefined);

        trackEvent(MixpanelEvent.AssistantMessageSent, {
            "Conversation Id": conversationId,
            "Is Default Suggestion": isDefaultSuggestion,
            "Feature": featureValue,
            "Has Files": completedFiles.length > 0,
            "Has Selected Text": !!targetedReply,
            "Assistant Mode": MIXPANEL_ASSISTANT_MODE[assistantMode]
        });

        mutate({
            conversationId,
            content,
            files: completedFiles,
            messageId: uuidv4(),
            abortController: new AbortController(),
            context: context ?? promptOptions?.promptContext,
            sources: sources ?? promptOptions?.sources,
            promptFeature: promptFeature ?? promptOptions?.promptFeature,
            productId: trackingAppIdentifier,
            additionalInstructions: additionalInstructions ?? promptOptions?.additionalInstructions,
            disableSearch: disableSearch || promptOptions?.disableSearch || false,
            assistantMode,
            dataLlmEnrichment: true,
            disabledChannels,
            pageActions: pageActions ?? promptOptions?.pageActions,
            selectedSkills: derivedSelectedSkills,
            llmId: llmId ?? promptOptions?.llmId,
            resume,
            preApprovedTools: preApprovedTools ?? promptOptions?.preApprovedTools,
            targetedReply,
            onMutate,
            // Refetch so the approval, question or connection request comes back if it is still open. A stream error or a block means core already took the reply.
            onError: hasCancelled
                ? error => {
                    queryClient.invalidateQueries({ queryKey: AiQueryKeys.GetConversation(conversationId) });
                    if (!(error instanceof StreamError || error instanceof BlockedError)) {
                        useComposerDraftStore.getState().restoreUnsentReply(conversationId, content, completedFiles);
                    }
                    onError?.();
                }
                : onError,
            onSettled
        });

        // Clear the targeted reply after sending the message
        if (targetedReply) {
            reset();
        }
    }, [mutate, promptOptions, trackEvent, reset, targetedReply, trackingAppIdentifier, queryClient, assistantMode, disabledChannels, isSlashCommandPickerEnabled]);

    return {
        createConversationMessage
    };
};
